import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ghostAccentIconTileClass,
} from '@up2stay/up2promo-ui';
import {
  ChevronRight,
  ChevronsUpDown,
  Clapperboard,
  HelpCircle,
  ListChecks,
  LogOut,
  Megaphone,
  Plus,
  Settings,
  UserRoundSearch,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PlanUpgradeTeaser } from '@/core/components/billing/plan-upgrade-teaser';
import { PlanLockBadge } from '@/core/components/billing/upgrade-surfaces';
import { LogoutConfirmDialog } from '@/core/components/logout-confirm-dialog';
import {
  createMobileMoreHubSections,
  isMobileMoreHubGroup,
  type MobileMoreHubGroupItem,
  type MobileMoreHubItem,
  type MobileMoreHubSubItem,
} from '@/core/components/navigation/mobile-more-hub-sections';
import {
  formatNavAttentionBadge,
  NAV_ATTENTION_BADGE_CLASS,
  navAttentionCountForTitleKey,
} from '@/core/components/navigation/nav-attention';
import { ThemeSwitcher } from '@/core/components/navigation/theme-switcher';
import { switcherAvatarFallbackClass } from '@/core/components/navigation/switcher-avatar-styles';
import { PageHeader } from '@/core/components/page-header';
import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
  SETTINGS_GROUP_ROW_SEPARATOR_CLASS,
} from '@/core/components/settings/settings-group';
import { SettingsPageShell } from '@/core/components/settings/settings-page-shell';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { GUIDANCE_TARGET } from '@/core/guidance/constants';
import { useGuidanceStore } from '@/core/guidance/guidance.store';
import { shouldMountTourKitPro } from '@/core/guidance/tour-kit-license';
import { useIntercom } from '@/core/hooks/use-intercom';
import { useIsPlatformAdmin } from '@/core/hooks/use-is-platform-admin';
import { useLogout } from '@/core/hooks/use-logout';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import {
  useNavAttentionCounts,
  type NavAttentionCounts,
} from '@/core/hooks/use-nav-attention-counts';
import { useNotificationHubBadgeCount } from '@/core/hooks/use-notifications';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useTouchTapToggle } from '@/core/hooks/use-touch-tap-toggle';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { workspaceCreatePath } from '@/core/lib/workspace-create-return';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';
import {
  useCurrentWorkspace,
  useSetWorkspace,
} from '@/core/store/workspace.store';

/** Quiet row label — settings density (not essay body). */
const HUB_LABEL_CLASS = 'text-[15px] font-medium leading-snug text-foreground';

const HUB_MUTED_CLASS = 'text-[13px] leading-snug text-muted-foreground';

const HUB_ROW_CLASS =
  'flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left transition-[color,background-color] duration-150 hover:bg-[color:var(--ghost-accent-bg)]/40 sm:px-5';

const HUB_ROW_ACTIVE_CLASS =
  'bg-[color:var(--ghost-accent-bg)] text-[color:var(--ghost-accent-fg)]';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function isHubPathActive(pathname: string, targetPath: string): boolean {
  const cleanPath = pathname.split('?')[0] ?? pathname;
  return cleanPath === targetPath || cleanPath.startsWith(`${targetPath}/`);
}

function HubIconTile({ children }: { children: ReactNode }) {
  return (
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-[8px]',
        ghostAccentIconTileClass,
      )}
    >
      {children}
    </span>
  );
}

type HubNavRowProps = {
  item: Extract<MobileMoreHubItem, { kind: 'link' }>;
  label: string;
  badgeCount?: number;
  badgeAriaLabel?: string;
  onNavigate: (path: string) => void;
  separated?: boolean;
};

function HubNavRow({
  item,
  label,
  badgeCount,
  badgeAriaLabel,
  onNavigate,
  separated = false,
}: HubNavRowProps) {
  const Icon = item.icon;
  const guidanceTarget =
    item.path === shellPaths.moreProducts
      ? GUIDANCE_TARGET.navProducts
      : undefined;
  const badgeText =
    badgeCount != null && badgeCount > 0
      ? formatNavAttentionBadge(badgeCount)
      : undefined;

  return (
    <SettingsGroupRow separated={separated} className="p-0">
      <button
        type="button"
        onClick={() => onNavigate(item.path)}
        className={HUB_ROW_CLASS}
        {...(guidanceTarget ? { 'data-guidance-target': guidanceTarget } : {})}
      >
        <HubIconTile>
          <Icon className="size-4" />
        </HubIconTile>
        <span className={HUB_LABEL_CLASS}>{label}</span>
        {item.requiredFeature ? (
          <PlanLockBadge feature={item.requiredFeature} />
        ) : null}
        {badgeText ? (
          <Badge
            variant="default"
            className={cn('ml-auto', NAV_ATTENTION_BADGE_CLASS)}
            aria-label={badgeAriaLabel}
          >
            {badgeText}
          </Badge>
        ) : (
          <ChevronRight className="ml-auto size-4 shrink-0 text-muted-foreground" />
        )}
      </button>
    </SettingsGroupRow>
  );
}

type HubNavAccordionGroupProps = {
  item: MobileMoreHubGroupItem;
  label: string;
  resolveLabel: (titleKey: string) => string;
  attentionCounts: NavAttentionCounts;
  resolveAttentionAria: (titleKey: string, count: number) => string | undefined;
  onNavigate: (path: string) => void;
  separated?: boolean;
};

function HubNavAccordionGroup({
  item,
  label,
  resolveLabel,
  attentionCounts,
  resolveAttentionAria,
  onNavigate,
  separated = false,
}: HubNavAccordionGroupProps) {
  const location = useLocation();
  const Icon = item.icon;
  const isChildActive = useMemo(
    () =>
      item.children.some((child) =>
        isHubPathActive(location.pathname, child.path),
      ),
    [item.children, location.pathname],
  );
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? isChildActive;
  const groupCount = navAttentionCountForTitleKey(
    item.titleKey,
    attentionCounts,
  );
  const groupBadge = formatNavAttentionBadge(groupCount);

  return (
    <SettingsGroupRow separated={separated} className="p-0">
      <Collapsible
        open={open}
        onOpenChange={setUserOpen}
        className="group/collapsible"
      >
        <CollapsibleTrigger asChild>
          <button type="button" className={HUB_ROW_CLASS}>
            <HubIconTile>
              <Icon className="size-4" />
            </HubIconTile>
            <span className={HUB_LABEL_CLASS}>{label}</span>
            {item.requiredFeature ? (
              <PlanLockBadge feature={item.requiredFeature} />
            ) : null}
            {groupBadge ? (
              <Badge
                variant="default"
                className={cn('ml-auto', NAV_ATTENTION_BADGE_CLASS)}
                aria-label={resolveAttentionAria(item.titleKey, groupCount)}
              >
                {groupBadge}
              </Badge>
            ) : null}
            <ChevronRight
              className={cn(
                'size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90',
                groupBadge ? 'ml-1.5' : 'ml-auto',
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col bg-muted/15 pb-0.5">
            {item.children.map((child, childIndex) => {
              const childCount = navAttentionCountForTitleKey(
                child.titleKey,
                attentionCounts,
              );
              return (
                <HubNavSubRow
                  key={child.path}
                  item={child}
                  label={resolveLabel(child.titleKey)}
                  active={isHubPathActive(location.pathname, child.path)}
                  badgeCount={childCount}
                  badgeAriaLabel={resolveAttentionAria(
                    child.titleKey,
                    childCount,
                  )}
                  onNavigate={onNavigate}
                  separated={childIndex > 0}
                />
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </SettingsGroupRow>
  );
}

type HubNavSubRowProps = {
  item: MobileMoreHubSubItem;
  label: string;
  active: boolean;
  badgeCount?: number;
  badgeAriaLabel?: string;
  onNavigate: (path: string) => void;
  separated?: boolean;
};

function HubNavSubRow({
  item,
  label,
  active,
  badgeCount = 0,
  badgeAriaLabel,
  onNavigate,
  separated = false,
}: HubNavSubRowProps) {
  const Icon = item.icon;
  const badgeText = formatNavAttentionBadge(badgeCount);

  return (
    <div className="flex flex-col">
      {separated ? (
        <div className={SETTINGS_GROUP_ROW_SEPARATOR_CLASS} aria-hidden />
      ) : null}
      <button
        type="button"
        onClick={() => onNavigate(item.path)}
        className={cn(
          'flex min-h-10 w-full items-center gap-3 py-2.5 pl-12 pr-4 text-left transition-[color,background-color] duration-150 hover:bg-[color:var(--ghost-accent-bg)]/40 sm:pr-5',
          active && HUB_ROW_ACTIVE_CLASS,
        )}
      >
        <Icon
          className={cn(
            'size-4 shrink-0 text-muted-foreground',
            active && 'text-[color:var(--brand-active-icon)]',
          )}
        />
        <span
          className={cn(
            HUB_LABEL_CLASS,
            active ? 'font-semibold' : 'font-medium',
          )}
        >
          {label}
        </span>
        {item.requiredFeature ? (
          <PlanLockBadge feature={item.requiredFeature} />
        ) : null}
        {badgeText ? (
          <Badge
            variant="default"
            className={cn('ml-auto', NAV_ATTENTION_BADGE_CLASS)}
            aria-label={badgeAriaLabel}
          >
            {badgeText}
          </Badge>
        ) : null}
      </button>
    </div>
  );
}

type HubActionRowProps = {
  label: string;
  icon: typeof Clapperboard;
  onClick: () => void;
  separated?: boolean;
  tone?: 'default' | 'destructive';
};

function HubActionRow({
  label,
  icon: Icon,
  onClick,
  separated = false,
  tone = 'default',
}: HubActionRowProps) {
  return (
    <SettingsGroupRow separated={separated} className="p-0">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          HUB_ROW_CLASS,
          tone === 'destructive'
            ? 'text-destructive hover:bg-destructive/10'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <Icon className="size-4 shrink-0" />
        <span
          className={cn(
            HUB_LABEL_CLASS,
            tone === 'destructive' && 'text-destructive',
          )}
        >
          {label}
        </span>
      </button>
    </SettingsGroupRow>
  );
}

export function MoreHubPage() {
  const dict = useDictionary();
  const navDict = dict.nav;
  const user = useAuthStore((state) => state.user);
  const workspace = useCurrentWorkspace();
  const setWorkspace = useSetWorkspace();
  const { workspaces } = useWorkspaces();
  const { navigateShell } = useNavigateShell();
  const { dialogOpen, setDialogOpen, requestLogout, confirmLogout } =
    useLogout();
  const { badgeCount } = useNotificationHubBadgeCount();
  const attentionCounts = useNavAttentionCounts();
  const { can } = usePermissions();
  const intercom = useIntercom();
  const isPlatformAdmin = useIsPlatformAdmin();
  const showTourKitProTools = shouldMountTourKitPro();
  const { accountItems, opsSection, domainSections } =
    createMobileMoreHubSections(can);
  const {
    open: workspaceSwitcherOpen,
    onOpenChange: onWorkspaceSwitcherOpenChange,
    triggerRef: workspaceSwitcherTriggerRef,
    triggerProps: workspaceSwitcherTriggerProps,
  } = useTouchTapToggle<HTMLButtonElement>();

  const userDisplayName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
      user.email ||
      'User'
    : 'User';
  const userInitials = user ? getInitials(userDisplayName) : 'U';

  const navAction = (path: string) => {
    navigateShell(path);
  };

  const resolveNavLabel = (titleKey: string) =>
    navDict[titleKey as keyof typeof navDict] ?? titleKey;

  const resolveAttentionAria = (titleKey: string, count: number) => {
    if (count <= 0) return undefined;
    const single = count === 1;
    const template =
      titleKey === 'leads'
        ? single
          ? navDict.attentionLeadsOne
          : navDict.attentionLeads
        : titleKey === 'feedback' ||
            titleKey === 'feedbackActivity' ||
            titleKey === 'feedbackInbox'
          ? navDict.attentionFeedback
          : titleKey === 'reputation' || titleKey === 'reviews'
            ? single
              ? navDict.attentionReviewsOne
              : navDict.attentionReviews
            : titleKey === 'products'
              ? single
                ? navDict.attentionClaimsOne
                : navDict.attentionClaims
              : undefined;
    return template?.replace('{count}', String(count));
  };

  const handleSignOut = () => {
    setTimeout(confirmLogout, 0);
  };

  const renderHubSection = (
    section: NonNullable<typeof opsSection> | (typeof domainSections)[number],
  ) => (
    <SettingsSection
      key={section.titleKey}
      id={`more-hub-${section.titleKey}`}
      title={resolveNavLabel(section.titleKey)}
      grouped
    >
      <SettingsGroupRows>
        {section.items.map((item, index) => {
          const label = resolveNavLabel(item.titleKey);
          const separated = index > 0;
          if (isMobileMoreHubGroup(item)) {
            return (
              <HubNavAccordionGroup
                key={item.titleKey}
                item={item}
                label={label}
                resolveLabel={resolveNavLabel}
                attentionCounts={attentionCounts}
                resolveAttentionAria={resolveAttentionAria}
                onNavigate={navAction}
                separated={separated}
              />
            );
          }
          const attentionCount = navAttentionCountForTitleKey(
            item.titleKey,
            attentionCounts,
          );
          return (
            <HubNavRow
              key={item.path}
              item={item}
              label={label}
              badgeCount={
                item.showBadge
                  ? badgeCount
                  : attentionCount > 0
                    ? attentionCount
                    : undefined
              }
              badgeAriaLabel={
                item.showBadge
                  ? undefined
                  : resolveAttentionAria(item.titleKey, attentionCount)
              }
              onNavigate={navAction}
              separated={separated}
            />
          );
        })}
      </SettingsGroupRows>
    </SettingsSection>
  );

  const moreTitle = navDict.more ?? navDict.moreOptions;

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="min-w-0 overflow-x-hidden">
          <PageHeader title={moreTitle} breadcrumb={moreTitle} hideBack />

          <SettingsPageShell>
            <PlanUpgradeTeaser />

            {workspace && opsSection ? renderHubSection(opsSection) : null}

            <SettingsSection
              id="more-hub-account"
              title={navDict.yourAccount ?? navDict.account}
              grouped
            >
              <SettingsGroupRows>
                <SettingsGroupRow className="p-0">
                  <div className="flex items-center gap-3 px-4 py-3 sm:px-5">
                    <Avatar className="size-9 shrink-0 rounded-full shadow-[var(--shadow-brand-mark)]">
                      {user?.photo?.path && user.photo.path.trim() ? (
                        <AvatarImage
                          src={getAbsoluteFileUrl(user.photo.path)}
                          alt={userDisplayName}
                        />
                      ) : null}
                      <AvatarFallback
                        className={cn(
                          'text-sm font-semibold',
                          switcherAvatarFallbackClass,
                        )}
                      >
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          HUB_LABEL_CLASS,
                          'truncate font-semibold',
                        )}
                      >
                        {userDisplayName}
                      </p>
                      {user?.email ? (
                        <p className={cn(HUB_MUTED_CLASS, 'truncate')}>
                          {user.email}
                        </p>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      variant="ghostAccent"
                      size="icon"
                      className="size-9 shrink-0"
                      aria-label={navDict.userSettings ?? navDict.settings}
                      onClick={() => navAction(shellPaths.moreUserSettings)}
                    >
                      <Settings className="size-4" />
                    </Button>
                  </div>
                </SettingsGroupRow>
                {accountItems.map((item) => (
                  <HubNavRow
                    key={item.path}
                    item={item}
                    label={resolveNavLabel(item.titleKey)}
                    badgeCount={item.showBadge ? badgeCount : undefined}
                    onNavigate={navAction}
                    separated
                  />
                ))}
              </SettingsGroupRows>
            </SettingsSection>

            {isPlatformAdmin && !user?.impersonation ? (
              <SettingsSection
                id="more-hub-support-admin"
                title={navDict.supportTools}
                grouped
              >
                <SettingsGroupRows>
                  <HubNavRow
                    item={{
                      kind: 'link',
                      path: shellPaths.moreSupport,
                      icon: UserRoundSearch,
                      titleKey: 'supportTools',
                    }}
                    label={navDict.supportTools}
                    onNavigate={navAction}
                  />
                </SettingsGroupRows>
              </SettingsSection>
            ) : null}

            <SettingsSection
              id="more-hub-workspace"
              title={navDict.currentWorkspace ?? dict.workspace.workspaces}
              grouped
            >
              <SettingsGroupRows>
                <SettingsGroupRow className="p-0">
                  <DropdownMenu
                    modal={false}
                    open={workspaceSwitcherOpen}
                    onOpenChange={onWorkspaceSwitcherOpenChange}
                  >
                    <DropdownMenuTrigger asChild>
                      <button
                        ref={workspaceSwitcherTriggerRef}
                        type="button"
                        className={cn(HUB_ROW_CLASS, 'justify-start')}
                        {...workspaceSwitcherTriggerProps}
                      >
                        <Avatar className="size-8 rounded-[8px] shadow-[var(--shadow-brand-mark)]">
                          {workspace?.photo?.path &&
                          workspace.photo.path.trim() ? (
                            <AvatarImage
                              src={getAbsoluteFileUrl(workspace.photo.path)}
                              alt={workspace.name || 'Workspace'}
                            />
                          ) : null}
                          <AvatarFallback
                            className={cn(
                              'rounded-[8px] text-xs font-semibold',
                              switcherAvatarFallbackClass,
                            )}
                          >
                            {getInitials(
                              workspace?.name ||
                                dict.workspace.workspace ||
                                'WS',
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div className="grid min-w-0 flex-1 text-start leading-tight">
                          <span
                            className={cn(
                              HUB_LABEL_CLASS,
                              'truncate font-semibold',
                            )}
                          >
                            {workspace
                              ? (workspace.name ?? dict.workspace.workspace)
                              : (dict.sidebar?.noWorkspaceMessage ??
                                'Select a workspace')}
                          </span>
                          <span className={cn(HUB_MUTED_CLASS, 'truncate')}>
                            {navDict.switchWorkspace}
                          </span>
                        </div>
                        <ChevronsUpDown className="ms-auto size-4 shrink-0 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-0 rounded-lg sm:min-w-56"
                      align="start"
                      side="bottom"
                      sideOffset={4}
                    >
                      <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                        {dict.workspace.workspaces}
                      </DropdownMenuLabel>
                      {workspaces.length > 0 ? (
                        workspaces.map((ws) => (
                          <DropdownMenuItem
                            key={ws.id}
                            onClick={() => setWorkspace(ws)}
                            className={cn(
                              'gap-2 p-2',
                              workspace?.id === ws.id && HUB_ROW_ACTIVE_CLASS,
                            )}
                          >
                            <div className="flex size-6 items-center justify-center overflow-hidden rounded-sm border">
                              <Avatar className="size-6 rounded-sm">
                                {ws.photo?.path && ws.photo.path.trim() ? (
                                  <AvatarImage
                                    src={getAbsoluteFileUrl(ws.photo.path)}
                                    alt={ws.name || 'Workspace'}
                                  />
                                ) : null}
                                <AvatarFallback
                                  className={cn(
                                    'rounded-sm text-xs font-semibold',
                                    switcherAvatarFallbackClass,
                                  )}
                                >
                                  {getInitials(ws.name || 'WS')}
                                </AvatarFallback>
                              </Avatar>
                            </div>
                            <div className="flex min-w-0 flex-col">
                              <span className="truncate">
                                {ws.name || 'Workspace'}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {dict.workspace.workspace}
                              </span>
                            </div>
                            {workspace?.id === ws.id ? (
                              <span className="ms-auto text-xs text-muted-foreground">
                                ✓
                              </span>
                            ) : null}
                          </DropdownMenuItem>
                        ))
                      ) : (
                        <DropdownMenuItem
                          disabled
                          className="gap-2 p-2 text-muted-foreground"
                        >
                          <span className="text-xs">No workspace</span>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="gap-2 p-2"
                        onClick={() =>
                          navAction(
                            workspaceCreatePath(window.location.pathname),
                          )
                        }
                      >
                        <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                          <Plus className="size-4" />
                        </div>
                        <span className="font-medium text-muted-foreground">
                          {dict.workspace?.createNewWorkspace ??
                            dict.workspace?.createWorkspace ??
                            'Create workspace'}
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SettingsGroupRow>
              </SettingsGroupRows>
            </SettingsSection>

            <SettingsSection
              id="more-hub-appearance"
              title={dict.appearance.title}
              grouped
            >
              <SettingsGroupRows>
                <SettingsGroupRow>
                  <ThemeSwitcher variant="segmented" showLabel={false} />
                </SettingsGroupRow>
              </SettingsGroupRows>
            </SettingsSection>

            {workspace && domainSections.length > 0
              ? domainSections.map((section) => renderHubSection(section))
              : null}

            <SettingsGroup>
              <SettingsGroupRows>
                <HubActionRow
                  icon={Clapperboard}
                  label={dict.guidance.tours.watchIntro}
                  onClick={() => {
                    useGuidanceStore.getState().requestOrientationReplay();
                  }}
                  separated={false}
                />
                {showTourKitProTools ? (
                  <>
                    <HubActionRow
                      icon={Megaphone}
                      label={dict.guidance.announcements.whatsNew}
                      onClick={() => navAction(shellPaths.moreWhatsNew)}
                      separated
                    />
                    <HubActionRow
                      icon={ListChecks}
                      label={dict.guidance.checklist.reopen}
                      onClick={() => {
                        useGuidanceStore.getState().requestChecklistRestore();
                      }}
                      separated
                    />
                  </>
                ) : null}
                <SettingsGroupRow separated className="p-0">
                  <div className="flex items-stretch">
                    <button
                      type="button"
                      className={cn(
                        HUB_ROW_CLASS,
                        'flex-1 text-destructive hover:bg-destructive/10',
                      )}
                      onClick={requestLogout}
                    >
                      <LogOut className="size-4 shrink-0" />
                      <span className={cn(HUB_LABEL_CLASS, 'text-destructive')}>
                        {navDict.signOut}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={cn(
                        HUB_ROW_CLASS,
                        'ml-auto w-auto shrink-0 gap-2 text-[color:var(--ghost-accent-fg)] hover:bg-[color:var(--ghost-accent-bg)]/40',
                      )}
                      onClick={() => {
                        void intercom.openSupport();
                      }}
                    >
                      <HelpCircle className="size-4 shrink-0" />
                      <span className={HUB_LABEL_CLASS}>
                        {navDict.helpSupport}
                      </span>
                    </button>
                  </div>
                </SettingsGroupRow>
              </SettingsGroupRows>
            </SettingsGroup>
          </SettingsPageShell>
        </AppPageLayout>
      </AppPageContent>

      <LogoutConfirmDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onConfirm={handleSignOut}
      />
    </AppIonPage>
  );
}
